import React, { ComponentProps, ReactElement, useState } from 'react';

import PropTypes from 'prop-types';

import { getValidChildren } from '@adsk/alloy-react-helpers';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import AccordionItem from './AccordionItem';

type AccordionProps = StylableComponent<HTMLDivElement> & {
  initialExpanded?: number[];
  multiExpand?: boolean;
  renderItem?: (props: ComponentProps<typeof AccordionItem>) => ReactElement;
};
/**
 * An accordion component.
 */
const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      style,
      className,
      children,
      initialExpanded = [0],
      multiExpand,
      renderItem = (itemProps: ComponentProps<typeof AccordionItem>) => (
        <AccordionItem {...itemProps} />
      ),
      ...props
    },
    ref,
  ) => {
    const getExpandedItems = () =>
      initialExpanded.length > 1 && !multiExpand
        ? [Math.min(...initialExpanded)]
        : initialExpanded;

    const [expandedItems, setExpandedItems] = useState<number[]>(
      React.Children.toArray(children).length > 0 &&
        initialExpanded.length === 0
        ? []
        : getExpandedItems(),
    );

    function onClick(e: MouseEvent, index: number) {
      e.preventDefault();
      e.stopPropagation();

      if (expandedItems.includes(index)) {
        setExpandedItems(expandedItems.filter((i) => i !== index));
      } else if (!multiExpand) {
        setExpandedItems([index]);
      } else {
        setExpandedItems([index, ...expandedItems]);
      }
    }

    return (
      <div
        className={className}
        css={[
          {
            width: '100%',
          },
          style,
        ]}
        ref={ref}
        {...props}
      >
        {React.Children.map(getValidChildren(children), (child, index) =>
          renderItem({
            expanded: expandedItems.includes(index),
            onExpand: (e: MouseEvent) => onClick(e, index),
            ...child.props,
          }),
        )}
      </div>
    );
  },
);

Accordion.displayName = 'Accordion';

Accordion.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The accordion items that make up this Accordion */
  children: PropTypes.node,
  /** An array of item indexes to expand initially */
  initialExpanded: PropTypes.arrayOf(PropTypes.number.isRequired),
  /** Whether or not multiple items can be expanded at once */
  multiExpand: PropTypes.bool,
  /** Render custom accordian item */
  renderItem: PropTypes.func,
};

export default Accordion;
